import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getVSMs, getVSM, createVSM, updateVSM, deleteVSM, archiveResource } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { perfColor, perfColorDark } from '@/utils/styleHelpers';
import { emptyStateMsg } from '@/utils/styleHelpers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { GitBranch, Workflow } from 'lucide-react';
import { PageTabs } from '@/components/PageTabs';

const HUB_TABS = [
  { path: '/vsm', label: 'Value Stream', icon: GitBranch },
  { path: '/process-flow', label: 'Process Flow', icon: Workflow },
];
import {
  Plus,
  ArrowLeft,
  Search,
  Filter,
  Trash2,
  ArrowRight,
  Clock,
  Pause,
  TrendingUp,
  Edit,
  Save,
  X,
  Archive
} from 'lucide-react';

const industries = [
  { value: 'logistics', label: 'Logistics' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
];

// VSM List Page
export const VSMListPage = () => {
  const { isGuest } = useAuth();
  const [vsms, setVsms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');

  useEffect(() => {
    loadVSMs();
  }, [industryFilter]);

  const loadVSMs = async () => {
    try {
      const data = await getVSMs(industryFilter !== 'all' ? industryFilter : null);
      setVsms(data);
    } catch (error) {
      toast.error('Failed to load value stream maps');
    } finally {
      setLoading(false);
    }
  };

  const filteredVSMs = vsms.filter(v => 
    v.title.toLowerCase().includes(search.toLowerCase()) ||
    v.process_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vsm-list-page">
        <PageTabs tabs={HUB_TABS} testIdPrefix="vsm-tab" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Value Stream Mapping</h1>
            <p className="text-muted-foreground font-body mt-1">
              Visualize and optimize your process flows
            </p>
          </div>
          <Button asChild data-testid="new-vsm-btn" className={isGuest ? 'hidden' : ''}>
            <Link to="/vsm/new">
              <Plus className="h-4 w-4 mr-2" /> New VSM
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search value stream maps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-vsm"
            />
          </div>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-industry">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map((ind) => (
                <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredVSMs.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVSMs.map((vsm) => (
              <div 
                key={vsm.vsm_id} 
                className={`block ${isGuest ? 'cursor-default' : 'cursor-pointer'}`}
                data-testid={`vsm-card-${vsm.vsm_id}`}
                onClick={() => { if (!isGuest) { window.location.href = `/vsm/${vsm.vsm_id}`; } }}
              >
                <Card className="h-full card-hover">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <Badge className="capitalize">{vsm.industry}</Badge>
                      <Badge variant={vsm.status === 'final' ? 'default' : 'secondary'}>
                        {vsm.status}
                      </Badge>
                    </div>
                    <CardTitle className="mt-3 line-clamp-1">{vsm.title}</CardTitle>
                    <CardDescription>{vsm.process_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold">{vsm.stages?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Stages</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{vsm.total_cycle_time || 0}</p>
                        <p className="text-xs text-muted-foreground">Cycle (min)</p>
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${perfColor(vsm.efficiency, 80, 50)}`}>
                          {Math.round(vsm.efficiency || 0)}%
                        </p>
                        <p className="text-xs text-muted-foreground">Efficiency</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Card className="py-16">
            <CardContent className="text-center">
              <GitBranch className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No value stream maps found</h3>
              <p className="text-muted-foreground font-body mb-4">
                {emptyStateMsg(isGuest, search, 'Create your first value stream map')}
              </p>
              {!isGuest && (
                <Button asChild>
                  <Link to="/vsm/new">
                    <Plus className="h-4 w-4 mr-2" /> Create VSM
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

// Create VSM Page
export const CreateVSMPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    process_name: '',
    industry: 'logistics',
    description: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const vsm = await createVSM(formData);
      toast.success('VSM created successfully!');
      navigate(`/vsm/${vsm.vsm_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="create-vsm-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vsm')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">New Value Stream Map</h1>
            <p className="text-muted-foreground font-body">Document and analyze a process flow</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>VSM Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">VSM Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Order Fulfillment Current State"
                  required
                  data-testid="vsm-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="process_name">Process Name *</Label>
                <Input
                  id="process_name"
                  value={formData.process_name}
                  onChange={(e) => handleChange('process_name', e.target.value)}
                  placeholder="e.g., Order-to-Ship Process"
                  required
                  data-testid="vsm-process"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                  <SelectTrigger data-testid="vsm-industry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the process scope and boundaries..."
                  rows={3}
                  required
                  data-testid="vsm-description"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/vsm')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-vsm">
              {loading ? 'Creating...' : 'Create VSM'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

// VSM Detail Page
export const VSMDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [vsm, setVsm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newStage, setNewStage] = useState({ name: '', cycle_time: 0, wait_time: 0, value_added: true });
  const [editingStage, setEditingStage] = useState(null);
  const [editingVSM, setEditingVSM] = useState(false);
  const [vsmForm, setVsmForm] = useState({ title: '', process_name: '', description: '', industry: '' });

  useEffect(() => {
    loadVSM();
  }, [id]);

  const loadVSM = async () => {
    try {
      const data = await getVSM(id);
      setVsm(data);
      setVsmForm({
        title: data.title,
        process_name: data.process_name,
        description: data.description,
        industry: data.industry
      });
    } catch (error) {
      toast.error('Failed to load VSM');
      navigate('/vsm');
    } finally {
      setLoading(false);
    }
  };

  const saveVSMDetails = async () => {
    setSaving(true);
    try {
      await updateVSM(id, vsmForm);
      setVsm({ ...vsm, ...vsmForm });
      setEditingVSM(false);
      toast.success('VSM updated');
    } catch (error) {
      toast.error('Failed to update VSM');
    } finally {
      setSaving(false);
    }
  };

  const addStage = async () => {
    if (!newStage.name) return;
    
    setSaving(true);
    try {
      const stage = {
        ...newStage,
        stage_id: `stage_${Date.now()}`
      };
      const updatedStages = [...(vsm.stages || []), stage];
      await updateVSM(id, { stages: updatedStages });
      
      const totalCycle = updatedStages.reduce((sum, s) => sum + (s.cycle_time || 0), 0);
      const totalWait = updatedStages.reduce((sum, s) => sum + (s.wait_time || 0), 0);
      const efficiency = totalCycle + totalWait > 0 ? (totalCycle / (totalCycle + totalWait)) * 100 : 0;
      
      setVsm({ 
        ...vsm, 
        stages: updatedStages,
        total_cycle_time: totalCycle,
        total_wait_time: totalWait,
        efficiency
      });
      setNewStage({ name: '', cycle_time: 0, wait_time: 0, value_added: true });
      toast.success('Stage added');
    } catch (error) {
      toast.error('Failed to add stage');
    } finally {
      setSaving(false);
    }
  };

  const updateStage = async (stageId) => {
    if (!editingStage) return;
    
    setSaving(true);
    try {
      const updatedStages = vsm.stages.map(s => 
        s.stage_id === stageId ? { ...s, ...editingStage } : s
      );
      await updateVSM(id, { stages: updatedStages });
      
      const totalCycle = updatedStages.reduce((sum, s) => sum + (s.cycle_time || 0), 0);
      const totalWait = updatedStages.reduce((sum, s) => sum + (s.wait_time || 0), 0);
      const efficiency = totalCycle + totalWait > 0 ? (totalCycle / (totalCycle + totalWait)) * 100 : 0;
      
      setVsm({ 
        ...vsm, 
        stages: updatedStages,
        total_cycle_time: totalCycle,
        total_wait_time: totalWait,
        efficiency
      });
      setEditingStage(null);
      toast.success('Stage updated');
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  const removeStage = async (stageId) => {
    setSaving(true);
    try {
      const updatedStages = vsm.stages.filter(s => s.stage_id !== stageId);
      await updateVSM(id, { stages: updatedStages });
      
      const totalCycle = updatedStages.reduce((sum, s) => sum + (s.cycle_time || 0), 0);
      const totalWait = updatedStages.reduce((sum, s) => sum + (s.wait_time || 0), 0);
      const efficiency = totalCycle + totalWait > 0 ? (totalCycle / (totalCycle + totalWait)) * 100 : 0;
      
      setVsm({ 
        ...vsm, 
        stages: updatedStages,
        total_cycle_time: totalCycle,
        total_wait_time: totalWait,
        efficiency
      });
      toast.success('Stage removed');
    } catch (error) {
      toast.error('Failed to remove stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this VSM?')) return;
    
    try {
      await deleteVSM(id);
      toast.success('VSM deleted');
      navigate('/vsm');
    } catch (error) {
      toast.error('Failed to delete VSM');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this VSM? It can be restored from the Archive page.')) return;
    try {
      await archiveResource('vsm', id);
      toast.success('VSM archived');
      navigate('/vsm');
    } catch (error) {
      toast.error('Failed to archive VSM');
    }
  };

  const startEditingStage = (stage) => {
    setEditingStage({ ...stage });
  };

  const canEdit = isAdmin || vsm?.created_by === user?.user_id;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!vsm) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vsm-detail-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/vsm')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              {editingVSM ? (
                <div className="space-y-3">
                  <Input
                    value={vsmForm.title}
                    onChange={(e) => setVsmForm({ ...vsmForm, title: e.target.value })}
                    placeholder="VSM Title"
                    className="text-xl font-bold"
                  />
                  <Input
                    value={vsmForm.process_name}
                    onChange={(e) => setVsmForm({ ...vsmForm, process_name: e.target.value })}
                    placeholder="Process Name"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveVSMDetails} disabled={saving}>
                      <Save className="h-4 w-4 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingVSM(false)}>
                      <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="capitalize">{vsm.industry}</Badge>
                    <Badge variant={vsm.status === 'final' ? 'default' : 'secondary'}>{vsm.status}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold tracking-tight">{vsm.title}</h1>
                    {canEdit && (
                      <Button variant="ghost" size="icon" onClick={() => setEditingVSM(true)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-muted-foreground font-body mt-1">{vsm.process_name}</p>
                </>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleArchive} data-testid="archive-vsm-btn">
                <Archive className="h-4 w-4 mr-2" /> Archive
              </Button>
              <Button variant="destructive" size="icon" onClick={handleDelete} data-testid="delete-vsm-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Metrics */}
        <Card>
          <CardHeader>
            <CardTitle>Process Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-5 rounded-lg border border-blue-500/20 bg-blue-500/5 text-center">
                <p className="text-3xl font-bold text-blue-400">{vsm.stages?.length || 0}</p>
                <p className="text-sm text-blue-300/80 mt-1 uppercase tracking-wide font-semibold">Process Stages</p>
              </div>
              <div className="p-5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-center">
                <p className="text-3xl font-bold text-emerald-400">{vsm.total_cycle_time || 0}</p>
                <p className="text-sm text-emerald-300/80 mt-1 uppercase tracking-wide font-semibold">Cycle Time (min)</p>
              </div>
              <div className="p-5 rounded-lg border border-amber-500/20 bg-amber-500/5 text-center">
                <p className="text-3xl font-bold text-amber-400">{vsm.total_wait_time || 0}</p>
                <p className="text-sm text-amber-300/80 mt-1 uppercase tracking-wide font-semibold">Wait Time (min)</p>
              </div>
              <div className="p-5 rounded-lg border border-cyan-500/20 bg-cyan-500/5 text-center">
                <p className={`text-3xl font-bold ${perfColorDark(vsm.efficiency)}`}>
                  {Math.round(vsm.efficiency || 0)}%
                </p>
                <p className="text-sm text-cyan-300/80 mt-1 uppercase tracking-wide font-semibold">Efficiency</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Process Flow */}
        <Card>
          <CardHeader>
            <CardTitle>Process Flow</CardTitle>
            <CardDescription>{vsm.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {vsm.stages?.length > 0 ? (
              <div className="flex flex-wrap items-center gap-4">
                {vsm.stages.map((stage, idx) => (
                  <div key={stage.stage_id} className="flex items-center gap-4">
                    {editingStage?.stage_id === stage.stage_id ? (
                      <div className="p-4 border rounded-lg min-w-[200px] bg-blue-50 border-blue-200 space-y-2">
                        <Input
                          value={editingStage.name}
                          onChange={(e) => setEditingStage({ ...editingStage, name: e.target.value })}
                          placeholder="Stage Name"
                          className="font-semibold"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Cycle (min)</Label>
                            <Input
                              type="number"
                              value={editingStage.cycle_time}
                              onChange={(e) => setEditingStage({ ...editingStage, cycle_time: parseFloat(e.target.value) || 0 })}
                              min="0"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Wait (min)</Label>
                            <Input
                              type="number"
                              value={editingStage.wait_time}
                              onChange={(e) => setEditingStage({ ...editingStage, wait_time: parseFloat(e.target.value) || 0 })}
                              min="0"
                            />
                          </div>
                        </div>
                        <Select 
                          value={editingStage.value_added ? 'va' : 'nva'} 
                          onValueChange={(v) => setEditingStage({ ...editingStage, value_added: v === 'va' })}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="va">Value Added</SelectItem>
                            <SelectItem value="nva">Non-Value Added</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => updateStage(stage.stage_id)} disabled={saving}>
                            <Save className="h-3 w-3 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingStage(null)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className={`relative p-4 border rounded-sm min-w-[160px] ${stage.value_added ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                        {canEdit && (
                          <div className="absolute -top-2 -right-2 flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 rounded-full bg-white shadow-sm"
                              onClick={() => startEditingStage(stage)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 rounded-full bg-white shadow-sm text-red-500"
                              onClick={() => removeStage(stage.stage_id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        <h4 className="font-semibold text-sm mb-2">{stage.name}</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-1 text-emerald-700">
                            <Clock className="h-3 w-3" /> {stage.cycle_time} min
                          </div>
                          <div className="flex items-center gap-1 text-amber-700">
                            <Pause className="h-3 w-3" /> {stage.wait_time} min wait
                          </div>
                        </div>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {stage.value_added ? 'Value Added' : 'Non-Value Added'}
                        </Badge>
                      </div>
                    )}
                    {idx < vsm.stages.length - 1 && (
                      <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No stages defined yet. Add stages below.</p>
            )}
          </CardContent>
        </Card>

        {/* Add Stage */}
        {canEdit && (
          <Card>
            <CardHeader>
              <CardTitle>Add Process Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-5 gap-4 items-end">
                <div className="space-y-2">
                  <Label>Stage Name</Label>
                  <Input
                    value={newStage.name}
                    onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
                    placeholder="e.g., Pick Items"
                    data-testid="stage-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cycle Time (min)</Label>
                  <Input
                    type="number"
                    value={newStage.cycle_time}
                    onChange={(e) => setNewStage({ ...newStage, cycle_time: parseFloat(e.target.value) || 0 })}
                    min="0"
                    data-testid="stage-cycle"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wait Time (min)</Label>
                  <Input
                    type="number"
                    value={newStage.wait_time}
                    onChange={(e) => setNewStage({ ...newStage, wait_time: parseFloat(e.target.value) || 0 })}
                    min="0"
                    data-testid="stage-wait"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select 
                    value={newStage.value_added ? 'va' : 'nva'} 
                    onValueChange={(v) => setNewStage({ ...newStage, value_added: v === 'va' })}
                  >
                    <SelectTrigger data-testid="stage-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="va">Value Added</SelectItem>
                      <SelectItem value="nva">Non-Value Added</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addStage} disabled={saving || !newStage.name} data-testid="add-stage-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VSMListPage;
